import { c as _c } from "react/compiler-runtime";
import { createContext, type ReactNode, useContext, useEffect, useRef, useState } from 'react';
import type { ResourceContext, ResourceSelection } from '../types';
import { resourceKey } from '../utils/resource-presentation';
import useResourceContext from './use-resource-context';
type ResourceContextSelection = {
  /** Route-derived resource the chat is about (with the live selection merged in), or undefined off a resource page. */
  context: ResourceContext | undefined;
  /** Whether the context is currently attached to outgoing messages. */
  isActive: boolean;
  setActive: (active: boolean) => void;
  /** Drop the selected element from the context (e.g. removing the node crumb in the chip). */
  clearSelection: () => void;
  publishSelection: (selection: ResourceSelection | null) => void;
};

// Default for `ChatInput`s rendered outside a provider (disabled placeholder/skeleton inputs):
// no context, nothing to toggle.
const Context = createContext<ResourceContextSelection>({
  context: undefined,
  isActive: true,
  setActive: () => {},
  clearSelection: () => {},
  publishSelection: () => {}
});

// Fold the clicked element into the page's resource as a nested `selection`.
const applySelection = (context: ResourceContext | undefined, selected: ResourceSelection | null): ResourceContext | undefined => {
  if (!context || !selected) return context;
  if (context.kind === 'agent' || context.kind === 'playbook') {
    return {
      ...context,
      selection: selected
    };
  }
  return context;
};
const ResourceContextProvider = t0 => {
  const $ = _c(18);
  const {
    children,
    enabled: t1
  } = t0;
  const enabled = t1 === undefined ? true : t1;
  const baseContext = useResourceContext(enabled);
  const [isActive, setActive] = useState(true);
  const [selected, setSelected] = useState(null);
  let t2;
  let t3;
  if ($[0] !== enabled) {
    t2 = () => {
      if (!enabled) {
        return;
      }
      const onClick = e => {
        const target = e.target as HTMLElement | null;
        if (!target) {
          return;
        }
        const el = target.closest("[data-percy-context-id]");
        if (el?.dataset.percyContextId) {
          setSelected({
            id: el.dataset.percyContextId,
            name: el.dataset.percyContextLabel || null
          });
          return;
        }
        if (target.closest("[data-percy-context-container]")) {
          setSelected(null);
        }
      };
      document.addEventListener("click", onClick, true);
      return () => document.removeEventListener("click", onClick, true);
    };
    t3 = [enabled];
    $[0] = enabled;
    $[1] = t2;
    $[2] = t3;
  } else {
    t2 = $[1];
    t3 = $[2];
  }
  useEffect(t2, t3);
  let t4;
  if ($[3] !== baseContext) {
    t4 = baseContext ? `${baseContext.kind}:${resourceKey(baseContext)}` : null;
    $[3] = baseContext;
    $[4] = t4;
  } else {
    t4 = $[4];
  }
  const identity = t4;
  const seenIdentity = useRef(identity);
  let t5;
  let t6;
  if ($[5] !== identity) {
    t5 = () => {
      const replaced = seenIdentity.current !== null && seenIdentity.current !== identity;
      seenIdentity.current = identity;
      if (!replaced) {
        return;
      }
      setActive(true);
      setSelected(null);
    };
    t6 = [identity];
    $[5] = identity;
    $[6] = t5;
    $[7] = t6;
  } else {
    t5 = $[6];
    t6 = $[7];
  }
  useEffect(t5, t6);
  let t7;
  if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = () => setSelected(null);
    $[8] = t7;
  } else {
    t7 = $[8];
  }
  const clearSelection = t7;
  let t8;
  if ($[9] !== baseContext || $[10] !== selected) {
    t8 = applySelection(baseContext, selected);
    $[9] = baseContext;
    $[10] = selected;
    $[11] = t8;
  } else {
    t8 = $[11];
  }
  const context = t8;
  let t9;
  if ($[12] !== context || $[13] !== isActive) {
    t9 = {
      context,
      isActive,
      setActive,
      clearSelection,
      publishSelection: setSelected
    };
    $[12] = context;
    $[13] = isActive;
    $[14] = t9;
  } else {
    t9 = $[14];
  }
  const value = t9;
  let t10;
  if ($[15] !== children || $[16] !== value) {
    t10 = <Context.Provider value={value}>{children}</Context.Provider>;
    $[15] = children;
    $[16] = value;
    $[17] = t10;
  } else {
    t10 = $[17];
  }
  return t10;
};
const useResourceContextSelection = () => {
  const $ = _c(6);
  const {
    context,
    isActive,
    setActive,
    clearSelection
  } = useContext(Context);
  const t0 = isActive ? context : undefined;
  let t1;
  if ($[0] !== clearSelection || $[1] !== context || $[2] !== isActive || $[3] !== setActive || $[4] !== t0) {
    t1 = {
      context,
      isActive,
      setActive,
      clearSelection,
      effectiveContext: t0
    };
    $[0] = clearSelection;
    $[1] = context;
    $[2] = isActive;
    $[3] = setActive;
    $[4] = t0;
    $[5] = t1;
  } else {
    t1 = $[5];
  }
  return t1;
};
const usePublishContextSelection = selection => {
  const $ = _c(5);
  const {
    publishSelection
  } = useContext(Context);
  const id = selection?.id;
  const name = selection?.name ?? null;
  let t0;
  let t1;
  if ($[0] !== id || $[1] !== name || $[2] !== publishSelection) {
    t0 = () => {
      publishSelection(id ? {
        id,
        name
      } : null);
      return () => publishSelection(null);
    };
    t1 = [id, name, publishSelection];
    $[0] = id;
    $[1] = name;
    $[2] = publishSelection;
    $[3] = t0;
    $[4] = t1;
  } else {
    t0 = $[3];
    t1 = $[4];
  }
  useEffect(t0, t1);
  return publishSelection;
};
export { ResourceContextProvider, usePublishContextSelection, useResourceContextSelection };